import { useReducer, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type Status = 'idle' | 'submitting' | 'success' | 'error';
type Field = 'name' | 'email' | 'message' | 'company';

interface State {
  name: string;
  email: string;
  message: string;
  company: string;
  status: Status;
}

type Action = { type: 'field'; field: Field; value: string } | { type: 'status'; status: Status };

const initialState: State = { name: '', email: '', message: '', company: '', status: 'idle' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'field':
      return { ...state, [action.field]: action.value };
    case 'status':
      return action.status === 'success' ? { ...initialState, status: 'success' } : { ...state, status: action.status };
  }
}

const inputClass =
  'w-full bg-[#0b100e] border border-border px-4 py-3 font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors';

export const ContactForm = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const submitting = state.status === 'submitting';

  const setField =
    (field: Field) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      dispatch({ type: 'field', field, value: e.target.value });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    dispatch({ type: 'status', status: 'submitting' });
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: state.name, email: state.email, message: state.message, company: state.company }),
      });
      const data = (await res.json()) as { ok?: boolean };
      dispatch({ type: 'status', status: res.ok && data.ok ? 'success' : 'error' });
    } catch {
      dispatch({ type: 'status', status: 'error' });
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 text-left" noValidate>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          Company
          <input type="text" tabIndex={-1} autoComplete="off" value={state.company} onChange={setField('company')} />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow">{t('contact.form.name')}</span>
          <input type="text" required value={state.name} onChange={setField('name')} className={inputClass} placeholder={t('contact.form.namePlaceholder')} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow">{t('contact.form.email')}</span>
          <input type="email" required value={state.email} onChange={setField('email')} className={inputClass} placeholder={t('contact.form.emailPlaceholder')} />
        </label>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="eyebrow">{t('contact.form.message')}</span>
        <textarea required rows={5} maxLength={5000} value={state.message} onChange={setField('message')} className={`${inputClass} resize-y`} placeholder={t('contact.form.messagePlaceholder')} />
      </label>

      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? t('contact.form.sending') : t('contact.form.send')}
        </button>
        {state.status === 'success' ? (
          <p className="font-mono text-sm text-primary" role="status">✓ {t('contact.form.success')}</p>
        ) : null}
        {state.status === 'error' ? (
          <p className="font-mono text-sm text-destructive" role="alert">{t('contact.form.error')}</p>
        ) : null}
      </div>
    </form>
  );
};
